/**
 * AscUtils.js
 * -------------------------------------------------------------
 * 目的:
 *   React コンポーネントの this / props に依存しない "純粋関数" を集約する。
 *   今回は外線番号関連の関数 (externalNumber.js から移動) を第1弾として配置。
 *
 * 移動理由:
 *   - utils/ 配下に単独ファイルを置くより、共通関数の集約ポイントを明確化し保守性を高める。
 *   - クラス (AscComponent) 内メソッドと分離し、副作用のない関数を独立テストしやすくする。
 *   - 今後 numberWithCommas や secToTime など純粋フォーマット系の段階的移行余地を残す。
 *
 * 注意:
 *   - 外線番号バリデーションは「数字のみ・3〜128桁」を基準とする。
 *   - 直接 this や props へアクセスするコードは本ファイルに置かない。
 *
 * 今後の追加候補 (要検討):
 *   - format helpers (numberWithCommas, secToTime)
 *   - date/time 変換系ユーティリティ
 *   - 共通バリデーション正規表現 (メール、パスワードなど) の静的抽象化
 * -------------------------------------------------------------
 */

import { CALL_TEXT_DOWNLOAD } from '../config/constants';

/**
 * 電話番号（外線番号）バリデーション。
 * 仕様:
 *   - 半角数字のみ
 *   - 3桁以上
 *   - DBカラム長(128)を超えない
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export const EXTERNAL_NUMBER_MIN_LENGTH = 3;
export const EXTERNAL_NUMBER_MAX_LENGTH = 128;

export function isValidExternalNumber(value) {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim();
  if (!normalized) return false;

  const externalNumberPattern = new RegExp(
    `^\\d{${EXTERNAL_NUMBER_MIN_LENGTH},${EXTERNAL_NUMBER_MAX_LENGTH}}$`
  );
  return externalNumberPattern.test(normalized);
}

/**
 * 外線番号オブジェクト配列から value リストを抽出
 * @param {Array<{value:string}>} list
 * @returns {string[]}
 */
export function extractExternalValues(list) {
  if (!Array.isArray(list)) return [];
  return list.map(row => row.value);
}

/**
 * 外線番号配列から invalid な値のみ抽出
 * @param {Array<{value:string}>} list
 * @returns {string[]}
 */
export function detectInvalidExternalNumbers(list) {
  const values = extractExternalValues(list);
  return values.filter(v => !isValidExternalNumber(v));
}

/**
 * 初期化/再評価用ヘルパー: list から {values, invalids} を生成
 * @param {Array<{value:string}>} list
 * @returns {{values:string[], invalids:string[]}}
 */
export function evaluateExternalNumbers(list) {
  const values = extractExternalValues(list);
  const invalids = values.filter(v => !isValidExternalNumber(v));
  return { values, invalids };
}

/**
 * 通話テキストダウンロード用ファイル名をS3オブジェクトキーから生成
 * - Record/PhoneBookで同一ロジックにするため共通化
 * - 既存実装の挙動に合わせ、拡張子は「最初の.以降」を落として .txt を付与
 *
 * 例:
 * - buildCallTextDownloadFilename('prefix/MH100000009_123_456_in_20260101010101_20260101010202.mp3')
 *   → 'MH100000009_123_456_in_20260101010101_20260101010202.txt'
 * - buildCallTextDownloadFilename('') → 'call_text.txt'
 *
 * @param {string|null|undefined} s3ObjectKey - S3オブジェクトキー（例: 'prefix/file.mp3'）
 * @returns {string} ダウンロード用ファイル名（拡張子: .txt）
 */
export function buildCallTextDownloadFilename(s3ObjectKey) {
  const key = (s3ObjectKey || '').toString();
  const lastSegment = key.split('/').pop() || '';
  const base = lastSegment.split('.')[0] || '';
  return `${base || CALL_TEXT_DOWNLOAD.DEFAULT_BASENAME}${CALL_TEXT_DOWNLOAD.FILE_EXTENSION}`;
}

/**
 * presigned URL から S3 Object Key を推定して返す
 * 例: https://...amazonaws.com/prefix/file.mp3?... -> prefix/file.mp3
 *
 * @param {string|null|undefined} presignedUrl
 * @returns {string}
 */
export function inferS3ObjectKeyFromPresignedUrl(presignedUrl) {
  try {
    if (!presignedUrl) return '';
    // ブラウザ環境では URL が利用可能
    const url = new URL(presignedUrl);
    const pathname = typeof url.pathname === 'string' ? url.pathname : '';
    // pathname は先頭に '/' が付く
    const key = decodeURIComponent(pathname).replace(/^\/+/, '');

    if (process.env.NODE_ENV === 'development') {
      console.debug('S3 Object Key inference:', {
        key,
        pathname,
        presignedUrlLength: typeof presignedUrl === 'string' ? presignedUrl.length : null,
      });
    }

    return key;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      // presigned URL はクエリに署名情報を含むため、URL全文はログに出さない
      console.warn('presigned URLからS3 Object Keyの推定に失敗しました。', {
        message: e && e.message ? e.message : String(e),
        presignedUrlLength: typeof presignedUrl === 'string' ? presignedUrl.length : null,
      });

      console.debug('S3 Object Key inference failed:', {
        message: e && e.message ? e.message : String(e),
        presignedUrlLength: typeof presignedUrl === 'string' ? presignedUrl.length : null,
      });
    }
    return '';
  }
}

/**
 * 録音再生モーダルの state などから通話テキストDL用ファイル名を生成
 * - s3_object_key が取れないケースでも、url(presigned URL)から推定して同一命名に寄せる
 *
 * @param {{s3_object_key?: string, url?: string}|null|undefined} playbackState
 * @returns {string}
 */
export function buildCallTextDownloadFilenameFromPlaybackState(playbackState) {
  const s3ObjectKey = playbackState && playbackState.s3_object_key;
  if (s3ObjectKey) return buildCallTextDownloadFilename(s3ObjectKey);

  const inferredKey = inferS3ObjectKeyFromPresignedUrl(playbackState && playbackState.url);
  return buildCallTextDownloadFilename(inferredKey);
}

/**
 * 外線番号を数字のみに正規化する。
 * ハイフンや括弧などの区切り文字を除去し、純粋な数字列を返す。
 * バックエンド・フロント間での番号比較に使用する。
 *
 * 例:
 *   normalizeExternalNumber('03-1234-5678') → '0312345678'
 *   normalizeExternalNumber('(03)1234-5678') → '0312345678'
 *   normalizeExternalNumber('0312345678') → '0312345678'
 *   normalizeExternalNumber(null) → ''
 *
 * @param {string|null|undefined} value - 正規化する外線番号
 * @returns {string} 数字のみの文字列。入力が無効な場合は空文字列
 */
export function normalizeExternalNumber(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\D/g, '');
}

/**
 * RecACEplus 契約有無（親アカウントの契約フラグ）を判定する（Issue #349）。
 * 録音音声一覧の「通話分類」「顧客名」「通話要約」列・CSV・通話分類検索欄など、
 * サブユーザーの「RecACEplusを使用する」トグル（メニュー表示用の options.RecAceApp）に
 * 巻き込まれず契約有無だけで表示を判定したい箇所で使用する。
 *
 * RecAceContract はログイン時に Sign.js が付与する。再ログイン前の旧セッションには
 * 存在しないため、その場合は従来どおり RecAceApp にフォールバックする。
 *
 * @param {{RecAceContract?: boolean, RecAceApp?: boolean}|null|undefined} options - userInfo.options
 * @returns {boolean}
 */
export function hasRecAceContract(options) {
  if (!options) return false;
  return options.RecAceContract !== undefined ? !!options.RecAceContract : !!options.RecAceApp;
}
